import { useTrackers } from '@ps/web-shared-analytics';
import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router';

import useBonusGameQuery from '@swf/core/hooks/useBonusGameQuery';
import useCasinoBonusGame from '@swf/core/hooks/useCasinoBonusGame';
import type { SpinOfDayData } from '@swf/core/types/CasinoBonus';
import { encodeQuery } from '@swf/core/utils';

import useSpinOfTheDayBonusGameData from '../../../../hooks/useSpinOfTheDayGameData';
import Card from '../Card/Card';

import { TXTCLI_Play } from './CasinoBonusCard.messages.json';

type Variant = 'StandardSotd' | 'BonusRounds';

interface Props {
  variant: Variant;
}

const calculateSOTDTotalSpins = (tickets: SpinOfDayData[]) =>
  tickets.reduce((sum, { numberOfSpins }) => sum + numberOfSpins, 0);

const bonusGameUrl = '/prhub/featured/bonus-game';

const CasinoBonusCard: React.FC<Props> = ({ variant }) => {
  const history = useHistory();
  const { formatMessage } = useIntl();
  const { linkClicked } = useTrackers();
  const { standardTickets, standardBonusGame, bonusRoundTickets, bonusRoundsBonusGame } =
    useSpinOfTheDayBonusGameData();
  const tickets = variant === 'StandardSotd' ? standardTickets : bonusRoundTickets;
  const bonusGame = variant === 'StandardSotd' ? standardBonusGame : bonusRoundsBonusGame;
  const queryParams = useBonusGameQuery(bonusGame);
  const launchCallback = useCallback(
    () =>
      history.push({
        pathname: bonusGameUrl,
        search: encodeQuery({
          gameUrl: bonusGame?.gameURL,
          query: queryParams
        })
      }),
    [history, bonusGame, queryParams]
  );
  const launch = useCasinoBonusGame(launchCallback);
  const hidden = variant === 'BonusRounds' && !bonusGame?.gameURL;
  const onClick = () => {
    launch(bonusGame);
    linkClicked({
      destinationUrl: bonusGameUrl,
      linkText: variant
    });
  };

  return !hidden ? (
    <Card
      variant={variant}
      disabled={!bonusGame?.gameURL}
      onClick={onClick}
      totalSpins={calculateSOTDTotalSpins(tickets)}
      expiryTime={bonusGame?.expiryTime}
      label={formatMessage(TXTCLI_Play)}
    />
  ) : null;
};

export default CasinoBonusCard;
